import * as React from 'react';
import classNames from 'classnames';
import { ICheckboxProps, ICheckboxImperativeActions } from '../Checkbox.types';
import Link from '../../Link/viewer/Link';
import { getAriaAttributes } from '../../../core/commons/a11y';
import { getDataAttributes } from '../../../core/commons/utils';
import style from './style/CheckboxBasicSkin.scss';
import Checkmark from './assets/checkmark.svg';

const noop = () => {};

const Checkbox: React.ForwardRefRenderFunction<
  ICheckboxImperativeActions,
  ICheckboxProps
> = (props, ref) => {
  const {
    id,
    checked = false,
    linkLabel = '',
    link = {},
    label,
    value,
    required,
    isDisabled,
    readOnly = false,
    shouldShowValidityIndication,
    validateValueAndShowIndication = noop,
    setValidityIndication = noop,
    onBlur = noop,
    onFocus = noop,
    onChange = noop,
    onCheckedChange = noop,
    onClick = noop,
    onDblClick = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    name,
    ariaAttributes,
    className,
    isValid,
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(ref, () => {
    return {
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
      setCustomValidity: message => {
        if (message.type === 'message') {
          inputRef.current?.setCustomValidity(message.message);
        }
      },
      get isFocused() {
        return (
          !!inputRef.current && inputRef.current === document.activeElement
        );
      },
      get isChecked() {
        return !!inputRef.current && inputRef.current.checked;
      },
    };
  });

  const _onChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    if (readOnly) {
      return;
    }
    onCheckedChange(event.target.checked);
    validateValueAndShowIndication();
    onChange(event);
  };

  const _onBlur: React.FocusEventHandler<HTMLInputElement> = event => {
    setValidityIndication(true);
    onBlur(event);
  };

  const _onClick: React.MouseEventHandler<HTMLInputElement> = event => {
    if (!isDisabled) {
      onClick(event);
    }
  };

  const _onDblClick: React.MouseEventHandler<HTMLLabelElement> = event => {
    if (!isDisabled) {
      onDblClick(event);
    }
  };

  const _onMouseEnter: React.MouseEventHandler<HTMLLabelElement> = event => {
    if (!isDisabled) {
      onMouseEnter(event);
    }
  };

  const _onMouseLeave: React.MouseEventHandler<HTMLLabelElement> = event => {
    if (!isDisabled) {
      onMouseLeave(event);
    }
  };

  const containerClasses = classNames(style.CheckboxBasicSkin, {
    [style.disabled]: isDisabled,
    [style.validationIndication]: !!shouldShowValidityIndication,
  });

  return (
    <label
      id={id}
      {...getDataAttributes(props)}
      data-testid="checkbox"
      className={classNames(containerClasses, className)}
      onDoubleClick={_onDblClick}
      onMouseEnter={_onMouseEnter}
      onMouseLeave={_onMouseLeave}
    >
      <input
        data-testid="input"
        onClick={_onClick}
        ref={inputRef}
        type="checkbox"
        className={style.input}
        checked={checked}
        value={value}
        onChange={_onChange}
        onBlur={_onBlur}
        onFocus={onFocus}
        required={required}
        aria-required={required}
        aria-invalid={shouldShowValidityIndication ? !isValid : undefined}
        aria-checked={checked}
        disabled={isDisabled}
        name={name}
        {...getAriaAttributes(ariaAttributes)}
      />
      <span className={style.checkbox}></span>
      <div className={style.checkmark}>
        <Checkmark></Checkmark>
      </div>
      <div className={style.label}>
        <span data-testid="text" className={style.text}>
          {label}
        </span>
        <Link {...link} className={style.link}>
          <span data-testid="linkLabel" className={style.linkLabel}>
            {linkLabel}
          </span>
        </Link>
      </div>
    </label>
  );
};

export default React.forwardRef(Checkbox);
